import * as mutation from "./mutation-types";
import * as lcMutation from "@/store/levy-collections/mutation-types";
import * as lcpMutation from "@/store/levy-collection-providers/mutation-types";
import * as systemLogMutation from "@/store/system-logs/mutation-types";
import * as dashboardMutation from "@/store/dashboard/mutation-types";
import * as action from "./action-types";
import { SHOW_NOTIFICATION } from "@/store/notifications/mutation-types";
import { GLOBAL_ERROR } from "@/store/system/mutation-types";
import { tableHelper } from "@/helpers/table.helper";
import { deepCopy, deepCopyAxiosException } from "@/store/helpers";
import axios from "axios";

export const actions = {
  async [action.LIST]({ commit, state }) {
    commit(mutation.LIST_STARTED);
    try {
      const criteria = deepCopy(state.listing.criteria);
      criteria.levyCollectionId = state.listing.levyCollectionId;
      criteria.providerId = state.listing.providerId;
      const url = tableHelper.constructUrl("/api/invoices", state.listing.paging, criteria);
      const { headers, data } = await axios.get(url);
      commit(mutation.LIST_SET_CRITERIA_DESCRIPTION, tableHelper.getCriteriaInfo(headers));
      commit(mutation.LIST_SET_PAGING, tableHelper.getPagingFromHeaders(headers));
      commit(mutation.LIST_SUCCEEDED, data);
      commit(mutation.STATE_UNLOAD_SINGLE);
      return true;
    } catch (e) {
      commit(mutation.LIST_FAILED);
      commit(GLOBAL_ERROR, deepCopyAxiosException(e));
      return false;
    }
  },
  async [action.LIST_NEXT_PAGE]({ state, commit, dispatch }) {
    commit(mutation.LIST_SET_PAGING, { page: state.listing.paging.page + 1 });
    await dispatch(action.LIST);
  },
  async [action.LIST_PREVIOUS_PAGE]({ state, commit, dispatch }) {
    commit(mutation.LIST_SET_PAGING, { page: state.listing.paging.page - 1 });
    await dispatch(action.LIST);
  },

  async [action.EXPORT]({ commit, state }) {
    commit(mutation.EXPORT_STARTED);
    try {
      const criteria = deepCopy(state.listing.criteria);
      criteria.levyCollectionId = state.listing.levyCollectionId;
      criteria.providerId = state.listing.providerId;
      await axios.get(tableHelper.constructUrl("/api/exports/validate/invoices", criteria));
      commit(SHOW_NOTIFICATION, "Your export will commence shortly.");
      const url = tableHelper.constructUrl(`/api/exports/invoices`, criteria);
      tableHelper.downloadExportedSpreadsheet(url);
      commit(mutation.EXPORT_SUCCEEDED);
      return true;
    } catch (e) {
      commit(mutation.EXPORT_FAILED);
      commit(GLOBAL_ERROR, deepCopyAxiosException(e));
      return false;
    }
  },

  async [action.VIEW]({ commit, state }, id) {
    commit(mutation.VIEW_STARTED, id);
    try {
      const url = `/api/invoices/${id}`;
      const { data } = await axios.get(url);
      commit(mutation.VIEW_SUCCEEDED, data);
      const index = tableHelper.getIndexOfId(state.listing, state.view.data.id);
      commit(mutation.VIEW_NEXT, tableHelper.getNextRecordInfo(index, state.listing));
      commit(mutation.VIEW_PREVIOUS, tableHelper.getPreviousRecordInfo(index, state.listing));
      return true;
    } catch (e) {
      commit(mutation.VIEW_FAILED);
      commit(GLOBAL_ERROR, deepCopyAxiosException(e));
      return false;
    }
  },
  async [action.VIEW_REFRESH]({ state, dispatch }, invoiceId) {
    const needsRefreshed = (!state.view.loaded && !state.view.loading) || state.view.invoiceId != invoiceId;
    if (needsRefreshed) {
      await dispatch(action.VIEW, invoiceId);
    }
  },

  async [action.RECONCILE]({ commit }, id) {
    commit(mutation.RECONCILE_STARTED, id);
    try {
      const url = `/api/invoices/${id}/reconcile-with-sap`;
      await axios.put(url);
      commit(mutation.RECONCILE_SUCCEEDED);
      commit(mutation.STATE_UNLOAD);
      commit(lcMutation.STATE_UNLOAD);
      commit(lcpMutation.STATE_UNLOAD);
      commit(systemLogMutation.STATE_UNLOAD);
      commit(dashboardMutation.STATE_UNLOAD);
      commit(SHOW_NOTIFICATION, "Invoice has been reconciled with SAP.");
      return true;
    } catch (e) {
      commit(mutation.RECONCILE_FAILED);
      commit(GLOBAL_ERROR, deepCopyAxiosException(e));
      return false;
    }
  },

  async [action.RECONCILE_TESTING_TOOL]({ state, commit }, id) {
    commit(mutation.RECONCILE_TESTING_TOOL_STARTED, id);
    try {
      const url = `/api/invoices/${id}/reconcile-with-sap-for-testing`;
      await axios.put(url, state.reconcileTestingTool.data);
      commit(mutation.RECONCILE_TESTING_TOOL_SUCCEEDED);
      commit(mutation.STATE_UNLOAD);
      commit(lcMutation.STATE_UNLOAD);
      commit(lcpMutation.STATE_UNLOAD);
      commit(systemLogMutation.STATE_UNLOAD);
      commit(dashboardMutation.STATE_UNLOAD);
      commit(SHOW_NOTIFICATION, "Invoice has been updated with the SAP reconciliation details you entered.");
      return true;
    } catch (e) {
      commit(mutation.RECONCILE_TESTING_TOOL_FAILED, deepCopyAxiosException(e));
      return false;
    }
  },

  async [action.GET_TRANSACTIONS]({ commit }, invoiceId) {
    commit(mutation.GET_TRANSACTIONS_STARTED, invoiceId);
    try {
      const url = `/api/invoices/${invoiceId}/transactions`;
      const { data } = await axios.get(url);
      commit(mutation.GET_TRANSACTIONS_SUCCEEDED, { invoiceId, transactions: data });
      return true;
    } catch (e) {
      commit(mutation.GET_TRANSACTIONS_FAILED, invoiceId);
      commit(GLOBAL_ERROR, deepCopyAxiosException(e));
      return false;
    }
  },

  async [action.DUE_GET]({ commit }, invoiceId) {
    commit(mutation.DUE_GET_STARTED);
    try {
      const url = `/api/invoices/${invoiceId}/due-date`;
      const { data } = await axios.get(url);
      commit(mutation.DUE_GET_SUCCEEDED, data);
      return true;
    } catch (e) {
      commit(mutation.DUE_GET_FAILED, deepCopyAxiosException(e));
      return false;
    }
  },
  async [action.DUE_SAVE]({ commit, state }, invoiceId) {
    commit(mutation.DUE_SAVE_STARTED);
    try {
      const url = `/api/invoices/${invoiceId}/due-date`;
      await axios.put(url, state.due.data);
      commit(mutation.STATE_UNLOAD);
      commit(systemLogMutation.STATE_UNLOAD);
      commit(dashboardMutation.STATE_UNLOAD);
      commit(lcpMutation.STATE_UNLOAD);
      commit(mutation.DUE_SAVE_SUCCEEDED);
      return true;
    } catch (e) {
      commit(mutation.DUE_SAVE_FAILED, deepCopyAxiosException(e));
      return false;
    }
  }
};
